import React, { useMemo, useState } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Box, Button, Chip, TextField } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate, useSearchParams, matchPath } from "react-router-dom";
import CountryTabs from "../components/CountryTabs";
import {
  CountryCode,
  COUNTRIES,
  getCategoryById,
  getVendorsForCategory,
} from "../mockData/marketplaceMockData";
import "../Marketplace.scss";

const CategoryVendorsPage: React.FC = () => {
  const location = useLocation();
  const categoryMatch = matchPath(
    "/home-employee/marketplace/category/:categoryId",
    location.pathname
  );
  const categoryId = categoryMatch?.params.categoryId || "";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const country = (searchParams.get("country") as CountryCode) || "IN";

  const category = getCategoryById(categoryId || "");
  const countryLabel = COUNTRIES.find((c) => c.code === country)?.label || country;

  const vendors = useMemo(() => {
    if (!category) return [];
    const list = getVendorsForCategory(category.id, country);
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q) ||
        v.subCategories.some((s) => s.toLowerCase().includes(q))
    );
  }, [category, country, search]);

  const handleCountryChange = (code: CountryCode) => {
    setSearchParams({ country: code });
  };

  if (!category) {
    return (
      <Box className="marketplace_wrapper">
        <p>Category not found.</p>
        <Button onClick={() => navigate("/home-employee/marketplace")}>Back to Marketplace</Button>
      </Box>
    );
  }

  return (
    <Box className="marketplace_wrapper">
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Button
            className="breadcrumb_back"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/home-employee/marketplace?country=${country}`)}
          >
            Marketplace · {category.name} · {countryLabel}
          </Button>
        </Grid>

        <Grid xs={12} className="main_heading">
          <h5>
            {category.icon} {category.name}
          </h5>
          <p>{category.description}</p>
        </Grid>

        <Grid xs={12}>
          <CountryTabs activeCountry={country} onChange={handleCountryChange} />
        </Grid>

        <Grid xs={12}>
          <Box className="subcategory_filter_row">
            {category.subCategories.map((sub) => (
              <Chip key={sub} label={sub} size="small" className="filter_chip" />
            ))}
          </Box>
        </Grid>

        <Grid xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search vendors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="vendor_search"
          />
        </Grid>

        <Grid xs={12}>
          {vendors.length === 0 ? (
            <Box className="empty_state">
              <p>No vendors in this category for {countryLabel} yet.</p>
              <Button onClick={() => navigate(`/home-employee/marketplace?country=${country}`)}>
                Browse other categories
              </Button>
            </Box>
          ) : (
            <Box className="vendor_grid">
              {vendors.map((vendor) => (
                <Box
                  key={vendor.id}
                  className="vendor_card"
                  onClick={() =>
                    navigate(
                      `/home-employee/marketplace/vendor/${vendor.id}?category=${category.id}&country=${country}`
                    )
                  }
                >
                  <Box className="vendor_card_header">
                    <img src={vendor.logo} alt={vendor.name} className="vendor_logo" />
                    <Box>
                      <h6>{vendor.name}</h6>
                      {vendor.isPlatformVendor && (
                        <Chip size="small" label="Platform Partner" className="platform_chip" />
                      )}
                    </Box>
                  </Box>
                  <p className="vendor_highlight">{vendor.highlight}</p>
                  <p className="vendor_desc">{vendor.description}</p>
                  <Box className="vendor_tags">
                    {vendor.tags?.map((tag) => (
                      <span key={tag} className="tag">
                        {tag}
                      </span>
                    ))}
                  </Box>
                  <Box className="vendor_action_hint">
                    {vendor.integrationType === "EXTERNAL_LINK" && "Opens external site →"}
                    {vendor.integrationType === "IN_APP_FORM" && "In-app request form →"}
                    {vendor.integrationType === "EMBEDDED_UI" && "Use in-app experience →"}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CategoryVendorsPage;
